import React, { createContext, useContext, useState, useEffect } from 'react';
import { locationService } from '../services/api';
import { ensureMediaOriginLoaded } from '../services/siteConfig';

const BookingContext = createContext();

const DEFAULT_LOCATION_NAME = 'Chennai';

export const useBooking = () => {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBooking must be used within BookingProvider');
  }
  return context;
};

export const BookingProvider = ({ children }) => {
  const [locationReady, setLocationReady] = useState(false);
  const [bookingData, setBookingData] = useState({
    location: null,
    visitDate: null,
    tickets: [],
    addons: [],
    appliedPromo: null,
    billingInfo: null,
  });

  useEffect(() => {
    const setDefaultLocation = async () => {
      try {
        await ensureMediaOriginLoaded();

        const res = await locationService.getAll();
        const data = res.data;
        const list = Array.isArray(data) ? data : (data?.results ?? []);
        const chennai = list.find(
          (loc) =>
            (loc.city && loc.city.toLowerCase().includes('chennai')) ||
            (loc.name && loc.name.toLowerCase().includes('chennai'))
        );
        const defaultLoc = chennai || list[0] || null;
        if (defaultLoc) {
          setBookingData((prev) => ({ ...prev, location: defaultLoc }));
        }
      } catch (err) {
        console.error('Failed to load default location (Chennai):', err);
      } finally {
        setLocationReady(true);
      }
    };
    setDefaultLocation();
  }, []);

  const updateLocation = (location) => {
    setBookingData(prev => ({ ...prev, location }));
  };

  const updateVisitDate = (date) => {
    setBookingData(prev => ({ ...prev, visitDate: date }));
  };

  const updateTickets = (tickets) => {
    setBookingData(prev => ({ ...prev, tickets }));
  };

  const updateAddons = (addons) => {
    setBookingData(prev => ({ ...prev, addons }));
  };

  const updateBillingInfo = (info) => {
    setBookingData(prev => ({ ...prev, billingInfo: info }));
  };

  const setAppliedPromo = (promo) => {
    setBookingData(prev => ({ ...prev, appliedPromo: promo }));
  };

  const clearAppliedPromo = () => {
    setBookingData(prev => ({ ...prev, appliedPromo: null }));
  };

  const resetBooking = () => {
    setBookingData((prev) => ({
      ...prev,
      visitDate: null,
      tickets: [],
      addons: [],
      appliedPromo: null,
      billingInfo: null,
    }));
  };

  return (
    <BookingContext.Provider
      value={{
        bookingData,
        locationReady,
        updateLocation,
        updateVisitDate,
        updateTickets,
        updateAddons,
        updateBillingInfo,
        setAppliedPromo,
        clearAppliedPromo,
        resetBooking,
      }}
    >
      {children}
    </BookingContext.Provider>
  );
};
